// src/data/intensiveModules.js
// Pre-Marriage Intensive Curriculum - Complete Module Data (Modules 1-10)
// Visual Theme: Emerald/Teal (#059669) with Gold (#D97706) accents
// Total: 160 Questions across 10 Modules (16 questions per module average)

export const INTENSIVE_MODULE_DATA = {
  // ============================================================
  // MODULE 1: KINGDOM MARRIAGE PERSPECTIVE
  // ============================================================
  1: {
    name: "Kingdom Marriage Perspective",
    shortName: "Kingdom Perspective",
    description: "Understanding God's original design for marriage as a divine covenant, not a social arrangement.",
    icon: "👑",
    colorTheme: {
      primary: "#059669",    // Emerald
      secondary: "#D97706",  // Gold
      gradient: "from-emerald-600 to-teal-700"
    },
    scoringGuidance: {
      excellent: {
        range: [65, 80],
        level: "Strong Kingdom Foundation",
        message: "You demonstrate a solid understanding of marriage as a divine covenant with God at the center.",
        pastoralGuidance: "Continue building on this foundation. Your perspective aligns well with God's design for marriage."
      },
      developing: {
        range: [50, 64],
        level: "Growing Understanding",
        message: "You have a good foundation but some areas need deeper alignment with biblical principles.",
        pastoralGuidance: "Focus on studying Scripture together about God's purpose for marriage. Consider what beliefs may still be culturally influenced."
      },
      vulnerable: {
        range: [16, 49],
        level: "Foundation Needs Strengthening",
        message: "Your perspective on marriage may be more cultural or contemporary than kingdom-centered.",
        pastoralGuidance: "This is a critical area. Take time to study Genesis 2, Ephesians 5, and Matthew 19 together. Discuss what marriage truly means before God."
      }
    },
    questions: [
      {
        id: 1,
        text: "Marriage is a lifelong covenant before God.",
        insight: "Reveals whether you view marriage as a permanent divine covenant or a temporary arrangement.",
        answerFormat: "agreement"
      },
      {
        id: 2,
        text: "Divorce should never be considered lightly.",
        insight: "Indicates the weight you place on the permanence of the marriage covenant.",
        answerFormat: "agreement"
      },
      {
        id: 3,
        text: "Marriage exists primarily to serve God's purpose.",
        insight: "Shows whether you see marriage as God-centered or self-centered.",
        answerFormat: "agreement"
      },
      {
        id: 4,
        text: "Personal happiness is more important than covenant faithfulness.",
        insight: "Reveals if personal fulfillment takes priority over covenant commitment.",
        answerFormat: "agreement",
        reverseScored: true
      },
      {
        id: 5,
        text: "Marriage requires sacrifice, not convenience.",
        insight: "Indicates readiness to embrace the sacrificial nature of marriage.",
        answerFormat: "agreement"
      },
      {
        id: 6,
        text: "God designed marriage with structure and order.",
        insight: "Shows acceptance of God's intentional design for marriage roles.",
        answerFormat: "agreement"
      },
      {
        id: 7,
        text: "Submission in marriage is alignment, not inferiority.",
        insight: "Reveals understanding of biblical submission as partnership under God's authority.",
        answerFormat: "agreement"
      },
      {
        id: 8,
        text: "Marriage should improve character over time.",
        insight: "Indicates expectation that marriage is a vehicle for spiritual growth.",
        answerFormat: "agreement"
      },
      {
        id: 9,
        text: "Marriage is meant to mature me spiritually.",
        insight: "Shows whether you view marriage as part of your sanctification journey.",
        answerFormat: "agreement"
      },
      {
        id: 10,
        text: "Leaving marriage is acceptable when it becomes difficult.",
        insight: "Reveals whether difficulty is seen as grounds for exit or growth.",
        answerFormat: "agreement",
        reverseScored: true
      },
      {
        id: 11,
        text: "Marriage is more than companionship.",
        insight: "Indicates understanding of marriage's deeper purpose beyond friendship.",
        answerFormat: "agreement"
      },
      {
        id: 12,
        text: "God's Word should guide marital decisions.",
        insight: "Shows commitment to Scripture as the authority for marriage.",
        answerFormat: "agreement"
      },
      {
        id: 13,
        text: "Marriage is not a solution to loneliness.",
        insight: "Reveals whether marriage is seen as a fix for personal emptiness.",
        answerFormat: "agreement"
      },
      {
        id: 14,
        text: "Commitment must exist beyond emotions.",
        insight: "Indicates understanding that covenant transcends feelings.",
        answerFormat: "agreement"
      },
      {
        id: 15,
        text: "Marriage mirrors Christ and the Church.",
        insight: "Shows grasp of the spiritual symbolism of marriage in Ephesians 5.",
        answerFormat: "agreement"
      },
      {
        id: 16,
        text: "I understand marriage as a divine calling.",
        insight: "Reveals whether marriage is seen as a sacred vocation from God.",
        answerFormat: "agreement"
      }
    ]
  },

  // ============================================================
  // MODULE 2: INTIMACY READINESS (INTIMATE FRAMEWORK)
  // ============================================================
  2: {
    name: "Intimacy That Sustains Marriage",
    shortName: "Intimacy Readiness",
    description: "Building intentional closeness through trust, safety, and shared life beyond romance and attraction.",
    icon: "💞",
    colorTheme: {
      primary: "#059669",
      secondary: "#D97706",
      gradient: "from-emerald-600 to-teal-700"
    },
    scoringGuidance: {
      excellent: {
        range: [73, 90],
        level: "Strong Intimacy Foundation",
        message: "You demonstrate healthy emotional safety and intentional closeness with your partner.",
        pastoralGuidance: "Continue nurturing this intimacy. Remember that closeness requires ongoing intentionality even after marriage."
      },
      developing: {
        range: [55, 72],
        level: "Growing Intimacy",
        message: "You have good connection but some areas of emotional safety or intentionality need development.",
        pastoralGuidance: "Identify which areas feel less safe and discuss them openly. Practice vulnerability in small ways consistently."
      },
      vulnerable: {
        range: [18, 54],
        level: "Intimacy Needs Attention",
        message: "Emotional closeness or safety may be significantly lacking in your relationship.",
        pastoralGuidance: "This is a critical area to address before marriage. Consider what barriers exist to feeling fully known and safe together."
      }
    },
    questions: [
      {
        id: 17,
        text: "I feel emotionally safe expressing vulnerability.",
        insight: "Reveals whether the relationship provides safety for emotional openness.",
        answerFormat: "agreement"
      },
      {
        id: 18,
        text: "I intentionally create time for connection.",
        insight: "Shows commitment to prioritizing closeness deliberately.",
        answerFormat: "frequency",
        options: [
          { value: 1, text: "Never - I don't prioritize this" },
          { value: 2, text: "Rarely - Only when convenient" },
          { value: 3, text: "Sometimes - When I remember" },
          { value: 4, text: "Often - I make regular effort" },
          { value: 5, text: "Always - It's a top priority" }
        ]
      },
      {
        id: 19,
        text: "I feel heard and understood by my partner.",
        insight: "Indicates whether communication creates genuine understanding.",
        answerFormat: "agreement"
      },
      {
        id: 20,
        text: "I avoid deep emotional conversations.",
        insight: "Reveals tendency to maintain emotional distance.",
        answerFormat: "agreement",
        reverseScored: true
      },
      {
        id: 21,
        text: "Emotional intimacy matters as much as physical intimacy.",
        insight: "Tests understanding that true intimacy is multidimensional.",
        answerFormat: "agreement"
      },
      {
        id: 22,
        text: "I can express needs without fear.",
        insight: "Measures safety to communicate personal needs openly.",
        answerFormat: "agreement"
      },
      {
        id: 23,
        text: "We invest in emotional closeness regularly.",
        insight: "Shows whether intentional connection is a priority.",
        answerFormat: "agreement"
      },
      {
        id: 24,
        text: "I feel emotionally connected beyond romance.",
        insight: "Tests depth of connection beyond physical attraction.",
        answerFormat: "agreement"
      },
      {
        id: 25,
        text: "I feel comfortable sharing weaknesses.",
        insight: "Reveals ability to be vulnerable about imperfections.",
        answerFormat: "agreement"
      },
      {
        id: 26,
        text: "I prefer to handle emotions privately.",
        insight: "Indicates tendency toward emotional isolation.",
        answerFormat: "agreement",
        reverseScored: true
      },
      {
        id: 27,
        text: "We prioritize closeness despite busyness.",
        insight: "Shows whether connection survives schedule pressure.",
        answerFormat: "agreement"
      },
      {
        id: 28,
        text: "Emotional distance concerns me.",
        insight: "Reveals awareness and concern about disconnection.",
        answerFormat: "agreement"
      },
      {
        id: 29,
        text: "I am intentional about nurturing intimacy.",
        insight: "Tests deliberate effort toward emotional closeness.",
        answerFormat: "agreement"
      },
      {
        id: 30,
        text: "I trust my partner with my emotions.",
        insight: "Measures emotional safety and trust.",
        answerFormat: "agreement"
      },
      {
        id: 31,
        text: "I feel secure being fully known.",
        insight: "Reveals comfort with complete transparency.",
        answerFormat: "agreement"
      },
      {
        id: 32,
        text: "I avoid emotional dependence.",
        insight: "Tests for unhealthy detachment patterns.",
        answerFormat: "agreement",
        reverseScored: true
      },
      {
        id: 33,
        text: "Intimacy requires consistent effort.",
        insight: "Shows understanding that intimacy is built, not automatic.",
        answerFormat: "agreement"
      },
      {
        id: 34,
        text: "Emotional closeness is a priority in marriage.",
        insight: "Tests the value placed on emotional connection.",
        answerFormat: "agreement"
      }
    ]
  },

  // ============================================================
  // MODULE 3: COMMUNICATION & CONFLICT MASTERY
  // ============================================================
  3: {
    name: "Communication & Conflict Mastery",
    shortName: "Communication & Conflict",
    description: "How godly couples disagree without destroying intimacy. Mastering conflict resolution and healthy communication.",
    icon: "💬",
    colorTheme: {
      primary: "#059669",
      secondary: "#D97706",
      gradient: "from-emerald-600 to-teal-700"
    },
    scoringGuidance: {
      excellent: {
        range: [73, 90],
        level: "Strong Communication Skills",
        message: "You demonstrate excellent conflict resolution abilities and healthy communication patterns.",
        pastoralGuidance: "Continue practicing these skills. Remember that even good communicators need ongoing intentionality."
      },
      developing: {
        range: [55, 72],
        level: "Growing Skills",
        message: "You have foundational communication skills but some patterns need refinement.",
        pastoralGuidance: "Identify your specific conflict triggers and develop strategies to address them before they escalate."
      },
      vulnerable: {
        range: [18, 54],
        level: "Communication Needs Work",
        message: "There are significant communication challenges that could cause major problems in marriage.",
        pastoralGuidance: "This area requires focused attention. Consider communication workshops or targeted counseling before marriage."
      }
    },
    questions: [
      {
        id: 35,
        text: "I address issues rather than avoid them.",
        insight: "Tests whether problems are confronted or swept under the rug.",
        answerFormat: "agreement"
      },
      {
        id: 36,
        text: "I raise my voice when frustrated.",
        insight: "Indicates emotional regulation during conflict.",
        answerFormat: "frequency",
        reverseScored: true,
        options: [
          { value: 1, text: "Always - I often shout" },
          { value: 2, text: "Often - Voice raises frequently" },
          { value: 3, text: "Sometimes - Occasionally lose control" },
          { value: 4, text: "Rarely - Almost never raise voice" },
          { value: 5, text: "Never - I stay calm" }
        ]
      },
      {
        id: 37,
        text: "I shut down during conflict.",
        insight: "Reveals stonewalling or withdrawal patterns.",
        answerFormat: "frequency",
        reverseScored: true,
        options: [
          { value: 1, text: "Always - I completely shut down" },
          { value: 2, text: "Often - I frequently withdraw" },
          { value: 3, text: "Sometimes - I occasionally go silent" },
          { value: 4, text: "Rarely - I mostly stay engaged" },
          { value: 5, text: "Never - I remain present and engaged" }
        ]
      },
      {
        id: 38,
        text: "I listen before defending myself.",
        insight: "Shows ability to receive before responding.",
        answerFormat: "frequency",
        options: [
          { value: 1, text: "Never - I immediately defend" },
          { value: 2, text: "Rarely - I usually defend first" },
          { value: 3, text: "Sometimes - Depends on the topic" },
          { value: 4, text: "Often - I try to listen first" },
          { value: 5, text: "Always - I fully hear before responding" }
        ]
      },
      {
        id: 39,
        text: "I seek resolution rather than victory.",
        insight: "Reveals whether conflict aims for peace or winning.",
        answerFormat: "agreement"
      },
      {
        id: 40,
        text: "I bring up past issues during arguments.",
        insight: "Indicates pattern of keeping score or rehashing history.",
        answerFormat: "frequency",
        reverseScored: true,
        options: [
          { value: 1, text: "Always - Past issues come up constantly" },
          { value: 2, text: "Often - I frequently reference the past" },
          { value: 3, text: "Sometimes - Occasionally bring up history" },
          { value: 4, text: "Rarely - I mostly stay on topic" },
          { value: 5, text: "Never - I focus only on current issues" }
        ]
      },
      {
        id: 41,
        text: "I apologize when wrong.",
        insight: "Shows humility and ability to take responsibility.",
        answerFormat: "custom",
        options: [
          { value: 1, text: "Never - I don't admit fault" },
          { value: 2, text: "Rarely - Only when forced" },
          { value: 3, text: "Sometimes - If clearly wrong" },
          { value: 4, text: "Often - I readily apologize" },
          { value: 5, text: "Always - I quickly own my mistakes" }
        ]
      },
      {
        id: 42,
        text: "I feel safe expressing disagreement.",
        insight: "Indicates safety to have differing opinions.",
        answerFormat: "agreement"
      },
      {
        id: 43,
        text: "Conflict helps growth when handled well.",
        insight: "Shows healthy perspective on disagreement as opportunity.",
        answerFormat: "agreement"
      },
      {
        id: 44,
        text: "I avoid conflict even when issues remain.",
        insight: "Reveals peace-faking vs genuine resolution.",
        answerFormat: "agreement",
        reverseScored: true
      },
      {
        id: 45,
        text: "I communicate respectfully under stress.",
        insight: "Shows ability to maintain honor during tension.",
        answerFormat: "custom",
        options: [
          { value: 1, text: "Never - I become disrespectful" },
          { value: 2, text: "Rarely - Respect breaks down under stress" },
          { value: 3, text: "Sometimes - Depends on the situation" },
          { value: 4, text: "Often - I maintain respect mostly" },
          { value: 5, text: "Always - I stay respectful regardless" }
        ]
      },
      {
        id: 46,
        text: "I can regulate my emotions during disagreement.",
        insight: "Indicates emotional self-control in conflict.",
        answerFormat: "custom",
        options: [
          { value: 1, text: "Never - Emotions overwhelm me" },
          { value: 2, text: "Rarely - I struggle significantly" },
          { value: 3, text: "Sometimes - Mixed success" },
          { value: 4, text: "Often - Good emotional control" },
          { value: 5, text: "Always - Excellent self-regulation" }
        ]
      },
      {
        id: 47,
        text: "I escalate conflict unintentionally.",
        insight: "Reveals patterns of making situations worse.",
        answerFormat: "frequency",
        reverseScored: true,
        options: [
          { value: 1, text: "Always - Conflicts always escalate" },
          { value: 2, text: "Often - Escalation is common" },
          { value: 3, text: "Sometimes - Occasional escalation" },
          { value: 4, text: "Rarely - Usually de-escalate" },
          { value: 5, text: "Never - I help calm situations" }
        ]
      },
      {
        id: 48,
        text: "I value peace over being right.",
        insight: "Shows priority of relationship over winning.",
        answerFormat: "agreement"
      },
      {
        id: 49,
        text: "I respond defensively to criticism.",
        insight: "Indicates receptivity to feedback.",
        answerFormat: "frequency",
        reverseScored: true,
        options: [
          { value: 1, text: "Always - Very defensive" },
          { value: 2, text: "Often - Usually get defensive" },
          { value: 3, text: "Sometimes - Depends on delivery" },
          { value: 4, text: "Rarely - Mostly receive well" },
          { value: 5, text: "Never - I receive criticism openly" }
        ]
      },
      {
        id: 50,
        text: "I am open to correction.",
        insight: "Shows teachability and humility.",
        answerFormat: "agreement"
      },
      {
        id: 51,
        text: "I resolve issues promptly.",
        insight: "Indicates speed of conflict resolution.",
        answerFormat: "custom",
        options: [
          { value: 1, text: "Never - Issues linger for weeks/months" },
          { value: 2, text: "Rarely - Takes a very long time" },
          { value: 3, text: "Sometimes - Some issues resolved quickly" },
          { value: 4, text: "Often - Most resolved within a day" },
          { value: 5, text: "Always - Same-day resolution priority" }
        ]
      },
      {
        id: 52,
        text: "I understand my conflict style.",
        insight: "Shows self-awareness about conflict patterns.",
        answerFormat: "agreement"
      }
    ]
  },

  // ============================================================
  // MODULE 4: TRUST, TRANSPARENCY & ACCOUNTABILITY
  // ============================================================
  4: {
    name: "Trust, Transparency & Accountability",
    shortName: "Trust & Transparency",
    description: "Building emotional safety and covenant integrity through consistent honesty, appropriate boundaries, and godly accountability.",
    icon: "🔓",
    colorTheme: {
      primary: "#059669",
      secondary: "#D97706",
      gradient: "from-emerald-600 to-teal-700"
    },
    scoringGuidance: {
      excellent: {
        range: [65, 80],
        level: "Strong Trust Foundation",
        message: "You demonstrate excellent transparency and mutual accountability. Your relationship is built on a solid foundation of trust.",
        pastoralGuidance: "Continue nurturing this trust. Maintain openness and invite accountability even when things are going well."
      },
      developing: {
        range: [50, 64],
        level: "Growing Trust",
        message: "You're developing healthy trust patterns, but some areas still need intentional work and vulnerability.",
        pastoralGuidance: "Focus on the specific areas where trust feels incomplete. Create safe spaces for difficult conversations."
      },
      vulnerable: {
        range: [16, 49],
        level: "Trust Needs Attention",
        message: "There are significant trust gaps that need to be addressed before marriage. Unresolved trust issues will intensify after the wedding.",
        pastoralGuidance: "Prioritize trust-building exercises. Address past hurts or patterns affecting current transparency. Extended counseling recommended."
      }
    },
    questions: [
      {
        id: 53,
        text: "I am fully transparent with my partner.",
        insight: "Measures commitment to openness and honesty in all areas of the relationship.",
        answerFormat: "agreement"
      },
      {
        id: 54,
        text: "I have nothing important to hide.",
        insight: "Reveals whether there are areas of secrecy that could undermine trust.",
        answerFormat: "agreement"
      },
      {
        id: 55,
        text: "I feel safe asking hard questions.",
        insight: "Tests whether the relationship allows for honest inquiry without defensiveness.",
        answerFormat: "agreement"
      },
      {
        id: 56,
        text: "I guard my phone or devices.",
        insight: "Reveals patterns of digital secrecy that can erode trust.",
        answerFormat: "agreement",
        reverseScored: true
      },
      {
        id: 57,
        text: "I am consistent in word and action.",
        insight: "Tests integrity and reliability - the foundation of trustworthiness.",
        answerFormat: "agreement"
      },
      {
        id: 58,
        text: "Trust must be earned through behavior.",
        insight: "Shows understanding that trust is built through consistent action over time.",
        answerFormat: "agreement"
      },
      {
        id: 59,
        text: "I feel emotionally secure in this relationship.",
        insight: "Measures the depth of emotional safety present in the relationship.",
        answerFormat: "agreement"
      },
      {
        id: 60,
        text: "I get defensive when questioned.",
        insight: "Reveals patterns of defensiveness that hinder transparency.",
        answerFormat: "agreement",
        reverseScored: true
      },
      {
        id: 61,
        text: "Financial openness is important to me.",
        insight: "Tests commitment to financial transparency - a major source of marital conflict.",
        answerFormat: "agreement"
      },
      {
        id: 62,
        text: "I value accountability in marriage.",
        insight: "Shows willingness to include outside support and correction.",
        answerFormat: "agreement"
      },
      {
        id: 63,
        text: "I resist being accountable.",
        insight: "Reveals independence or pride that rejects healthy oversight.",
        answerFormat: "agreement",
        reverseScored: true
      },
      {
        id: 64,
        text: "I trust my partner's integrity.",
        insight: "Measures confidence in partner's character and honesty.",
        answerFormat: "agreement"
      },
      {
        id: 65,
        text: "I am honest even when it costs me.",
        insight: "Tests commitment to truth even when it's uncomfortable.",
        answerFormat: "agreement"
      },
      {
        id: 66,
        text: "I fear being fully known.",
        insight: "Reveals barriers to vulnerability and emotional intimacy.",
        answerFormat: "agreement",
        reverseScored: true
      },
      {
        id: 67,
        text: "Transparency strengthens relationships.",
        insight: "Shows understanding that openness builds rather than threatens connection.",
        answerFormat: "agreement"
      },
      {
        id: 68,
        text: "I welcome godly oversight.",
        insight: "Tests receptivity to spiritual authority and mentorship.",
        answerFormat: "agreement"
      }
    ]
  },

  // ============================================================
  // MODULE 5: EXPECTATIONS, ROLES & GENDER NEEDS
  // ============================================================
  5: {
    name: "Expectations, Roles & Gender Needs",
    shortName: "Roles & Expectations",
    description: "Understanding what men and women truly need in marriage, aligning expectations, and embracing biblical roles without resentment.",
    icon: "⚖️",
    colorTheme: {
      primary: "#059669",
      secondary: "#D97706",
      gradient: "from-emerald-600 to-teal-700"
    },
    scoringGuidance: {
      excellent: {
        range: [65, 80],
        level: "Well-Aligned Expectations",
        message: "You have healthy, realistic expectations and a biblical understanding of marital roles. You're prepared to honor each other's unique needs.",
        pastoralGuidance: "Continue refining your understanding. Remember that roles are about function, not value - both are equally important to God."
      },
      developing: {
        range: [50, 64],
        level: "Developing Clarity",
        message: "Some expectations are aligned, but others need more discussion. You may have different views on roles that need resolution.",
        pastoralGuidance: "Create space for honest conversation about expectations. Study biblical passages on marriage roles together."
      },
      vulnerable: {
        range: [16, 49],
        level: "Expectation Gaps",
        message: "Significant misalignment in expectations or role understanding. Unaddressed, these gaps will cause frustration after marriage.",
        pastoralGuidance: "Prioritize clarifying expectations before marriage. Consider reading books on biblical marriage together. Counseling advised."
      }
    },
    questions: [
      {
        id: 69,
        text: "I feel valued by my partner.",
        insight: "Measures whether appreciation and worth are being communicated effectively.",
        answerFormat: "agreement"
      },
      {
        id: 70,
        text: "I feel respected by my partner.",
        insight: "Tests whether respect - a core need especially for men - is present.",
        answerFormat: "agreement"
      },
      {
        id: 71,
        text: "I express love in ways my partner understands.",
        insight: "Shows awareness that love must be given in the receiver's language.",
        answerFormat: "agreement"
      },
      {
        id: 72,
        text: "I expect my partner to love me my way.",
        insight: "Reveals self-centered expectations that ignore partner's differences.",
        answerFormat: "agreement",
        reverseScored: true
      },
      {
        id: 73,
        text: "I understand my partner's primary emotional needs.",
        insight: "Tests whether you've learned what truly makes your partner feel loved.",
        answerFormat: "agreement"
      },
      {
        id: 74,
        text: "I feel emotionally cared for.",
        insight: "Measures whether emotional nurturing is present in the relationship.",
        answerFormat: "agreement"
      },
      {
        id: 75,
        text: "I communicate appreciation often.",
        insight: "Shows pattern of verbal affirmation and gratitude.",
        answerFormat: "agreement"
      },
      {
        id: 76,
        text: "I feel taken for granted.",
        insight: "Reveals whether efforts and contributions go unacknowledged.",
        answerFormat: "agreement",
        reverseScored: true
      },
      {
        id: 77,
        text: "I admire my partner.",
        insight: "Tests presence of genuine respect and appreciation for partner's character.",
        answerFormat: "agreement"
      },
      {
        id: 78,
        text: "I affirm my partner verbally.",
        insight: "Measures whether positive words are regularly spoken.",
        answerFormat: "agreement"
      },
      {
        id: 79,
        text: "I feel secure in this relationship.",
        insight: "Tests overall sense of safety and stability in the partnership.",
        answerFormat: "agreement"
      },
      {
        id: 80,
        text: "I struggle to meet my partner's needs.",
        insight: "Reveals difficulty in understanding or fulfilling what partner requires.",
        answerFormat: "agreement",
        reverseScored: true
      },
      {
        id: 81,
        text: "I honor differences without resentment.",
        insight: "Shows ability to accept gender differences as design, not deficiency.",
        answerFormat: "agreement"
      },
      {
        id: 82,
        text: "I feel emotionally safe.",
        insight: "Measures the presence of emotional security in expressing needs.",
        answerFormat: "agreement"
      },
      {
        id: 83,
        text: "I understand biblical roles clearly.",
        insight: "Tests grasp of Ephesians 5 teaching on husband and wife roles.",
        answerFormat: "agreement"
      },
      {
        id: 84,
        text: "I resist role structure.",
        insight: "Reveals rejection or struggle with biblical marriage structure.",
        answerFormat: "agreement",
        reverseScored: true
      }
    ]
  },

  // ============================================================
  // MODULE 6: READINESS, RED FLAGS & CHECKPOINTS
  // ============================================================
  6: {
    name: "Readiness, Red Flags & Checkpoints",
    shortName: "Readiness Assessment",
    description: "Discerning maturity, alignment, and long-term sustainability. Ensuring you're entering covenant with clarity, not confusion.",
    icon: "🚦",
    colorTheme: {
      primary: "#059669",
      secondary: "#D97706",
      gradient: "from-emerald-600 to-teal-700"
    },
    scoringGuidance: {
      excellent: {
        range: [65, 80],
        level: "Ready for Marriage",
        message: "You demonstrate strong readiness indicators and have thoughtfully addressed potential concerns. You appear prepared for this covenant.",
        pastoralGuidance: "Continue your preparation with confidence. Stay vigilant and maintain the self-awareness you've developed."
      },
      developing: {
        range: [50, 64],
        level: "Mostly Ready",
        message: "You show good readiness in many areas, but some concerns need attention before proceeding.",
        pastoralGuidance: "Address the specific areas of concern before the wedding. Don't rush - better to delay than to enter unprepared."
      },
      vulnerable: {
        range: [16, 49],
        level: "Significant Concerns",
        message: "There are notable red flags or readiness gaps that must be addressed. Proceeding now would be unwise.",
        pastoralGuidance: "Extended engagement period recommended. Address red flags directly with professional help. Do not set a wedding date until concerns are resolved."
      }
    },
    questions: [
      {
        id: 85,
        text: "I am emotionally stable on my own.",
        insight: "Tests whether emotional health exists independent of the relationship.",
        answerFormat: "agreement"
      },
      {
        id: 86,
        text: "I am not marrying out of fear or pressure.",
        insight: "Reveals whether the decision is genuinely chosen or circumstantially forced.",
        answerFormat: "agreement"
      },
      {
        id: 87,
        text: "I handle disappointment well.",
        insight: "Tests emotional maturity and resilience under stress.",
        answerFormat: "agreement"
      },
      {
        id: 88,
        text: "I rush decisions under pressure.",
        insight: "Reveals impulsive patterns that can lead to poor choices.",
        answerFormat: "agreement",
        reverseScored: true
      },
      {
        id: 89,
        text: "I submit to godly authority.",
        insight: "Tests willingness to be under spiritual oversight and correction.",
        answerFormat: "agreement"
      },
      {
        id: 90,
        text: "I receive correction humbly.",
        insight: "Measures teachability and openness to feedback.",
        answerFormat: "agreement"
      },
      {
        id: 91,
        text: "I am disciplined in daily life.",
        insight: "Tests practical self-control and responsibility.",
        answerFormat: "agreement"
      },
      {
        id: 92,
        text: "I avoid responsibility.",
        insight: "Reveals patterns of irresponsibility that will affect marriage.",
        answerFormat: "agreement",
        reverseScored: true
      },
      {
        id: 93,
        text: "I am ready for lifelong commitment.",
        insight: "Tests understanding of marriage permanence.",
        answerFormat: "agreement"
      },
      {
        id: 94,
        text: "I am open to growth and change.",
        insight: "Measures flexibility and willingness to mature.",
        answerFormat: "agreement"
      },
      {
        id: 95,
        text: "I am marrying with clarity.",
        insight: "Tests whether the decision comes from peace, not confusion.",
        answerFormat: "agreement"
      },
      {
        id: 96,
        text: "I avoid difficult conversations.",
        insight: "Reveals avoidance patterns that prevent necessary discussions.",
        answerFormat: "agreement",
        reverseScored: true
      },
      {
        id: 97,
        text: "I have healed from major past wounds.",
        insight: "Tests whether previous trauma has been adequately addressed.",
        answerFormat: "agreement"
      },
      {
        id: 98,
        text: "I understand marriage responsibilities.",
        insight: "Measures grasp of what marriage actually requires.",
        answerFormat: "agreement"
      },
      {
        id: 99,
        text: "I rely on prayer for decisions.",
        insight: "Tests whether spiritual discernment guides major choices.",
        answerFormat: "agreement"
      },
      {
        id: 100,
        text: "I feel prepared for covenant.",
        insight: "Overall self-assessment of readiness for lifelong commitment.",
        answerFormat: "agreement"
      }
    ]
  },

  // ============================================================
  // MODULE 7: PURPOSE, CALLING & KINGDOM PARTNERSHIP
  // ============================================================
  7: {
    name: "Purpose, Calling & Kingdom Partnership",
    shortName: "Purpose & Calling",
    description: "Building a marriage that serves God's assignment. Marriage is not the purpose—it is a platform for purpose.",
    icon: "🎯",
    colorTheme: {
      primary: "#059669",
      secondary: "#D97706",
      gradient: "from-emerald-600 to-teal-700"
    },
    scoringGuidance: {
      excellent: {
        range: [57, 70],
        level: "Strong Kingdom Partnership",
        message: "You have a clear sense of divine purpose and your callings complement each other well. Your marriage is positioned to serve God's kingdom.",
        pastoralGuidance: "Continue refining your shared vision. Regularly revisit your purpose together and ensure your marriage serves something bigger than yourselves."
      },
      developing: {
        range: [43, 56],
        level: "Growing Clarity",
        message: "You're developing purpose alignment, but some areas need more intentional discussion about calling and direction.",
        pastoralGuidance: "Spend time discussing individual callings and how they intersect. Pray together for clarity on what God wants to build through your union."
      },
      vulnerable: {
        range: [14, 42],
        level: "Purpose Unclear",
        message: "There may be a lack of clarity about individual or shared purpose. Marriage without mission often becomes self-consuming.",
        pastoralGuidance: "This is important to address. A marriage without purpose drifts. Seek counsel to discover what God is calling you to build together."
      }
    },
    questions: [
      {
        id: 101,
        text: "I know God's purpose for my life.",
        insight: "Tests clarity of individual calling and divine direction.",
        answerFormat: "agreement"
      },
      {
        id: 102,
        text: "My partner supports my calling.",
        insight: "Measures whether your partner actively encourages your purpose.",
        answerFormat: "agreement"
      },
      {
        id: 103,
        text: "We share spiritual direction.",
        insight: "Tests alignment in spiritual vision and priorities.",
        answerFormat: "agreement"
      },
      {
        id: 104,
        text: "I lack clarity about my future.",
        insight: "Reveals uncertainty about direction and purpose.",
        answerFormat: "agreement",
        reverseScored: true
      },
      {
        id: 105,
        text: "Marriage supports my purpose.",
        insight: "Shows whether marriage is seen as a platform for calling.",
        answerFormat: "agreement"
      },
      {
        id: 106,
        text: "We pray together consistently.",
        insight: "Tests spiritual partnership and shared devotional life.",
        answerFormat: "agreement"
      },
      {
        id: 107,
        text: "We serve God together.",
        insight: "Measures active ministry partnership.",
        answerFormat: "agreement"
      },
      {
        id: 108,
        text: "I feel directionless.",
        insight: "Reveals lack of purpose or vision.",
        answerFormat: "agreement",
        reverseScored: true
      },
      {
        id: 109,
        text: "Our visions complement each other.",
        insight: "Tests whether individual callings work together harmoniously.",
        answerFormat: "agreement"
      },
      {
        id: 110,
        text: "We discuss long-term goals.",
        insight: "Shows intentionality about future planning together.",
        answerFormat: "agreement"
      },
      {
        id: 111,
        text: "God is central in our plans.",
        insight: "Tests whether decisions are submitted to divine guidance.",
        answerFormat: "agreement"
      },
      {
        id: 112,
        text: "We encourage each other spiritually.",
        insight: "Measures mutual spiritual support and growth.",
        answerFormat: "agreement"
      },
      {
        id: 113,
        text: "I see marriage as mission.",
        insight: "Shows whether marriage is viewed as a platform for kingdom work.",
        answerFormat: "agreement"
      },
      {
        id: 114,
        text: "Purpose guides our decisions.",
        insight: "Tests whether calling influences major life choices.",
        answerFormat: "agreement"
      }
    ]
  },

  // ============================================================
  // MODULE 8: SEXUAL INTEGRITY, DISCIPLINE & FIDELITY
  // ============================================================
  8: {
    name: "Sexual Integrity, Discipline & Fidelity",
    shortName: "Sexual Integrity",
    description: "Building self-control before marriage and faithfulness after marriage. Marriage does not cure lust—discipline must be built.",
    icon: "🛡️",
    colorTheme: {
      primary: "#059669",
      secondary: "#D97706",
      gradient: "from-emerald-600 to-teal-700"
    },
    scoringGuidance: {
      excellent: {
        range: [65, 80],
        level: "Strong Sexual Integrity",
        message: "You demonstrate healthy self-control and commitment to purity. This foundation will protect your marriage.",
        pastoralGuidance: "Continue guarding your heart and maintaining accountability. The patterns you build now will serve you for a lifetime."
      },
      developing: {
        range: [50, 64],
        level: "Growing Discipline",
        message: "You're developing self-control, but some areas need more intentional discipline and accountability.",
        pastoralGuidance: "Identify specific areas of vulnerability and build stronger boundaries. Accountability is essential during this season."
      },
      vulnerable: {
        range: [16, 49],
        level: "Integrity Needs Attention",
        message: "There are significant concerns about sexual discipline that must be addressed before marriage. Patterns formed now will continue.",
        pastoralGuidance: "This is a critical area. Seek specialized counseling if needed. Do not proceed until there is demonstrated, sustained change."
      }
    },
    questions: [
      {
        id: 115,
        text: "I practice sexual self-control.",
        insight: "Tests current discipline in managing sexual desires.",
        answerFormat: "agreement"
      },
      {
        id: 116,
        text: "I honor God with my body.",
        insight: "Measures commitment to bodily stewardship.",
        answerFormat: "agreement"
      },
      {
        id: 117,
        text: "I have accountability for sexual purity.",
        insight: "Tests whether outside support exists for maintaining integrity.",
        answerFormat: "agreement"
      },
      {
        id: 118,
        text: "I normalize sexual compromise.",
        insight: "Reveals justification patterns that erode boundaries.",
        answerFormat: "agreement",
        reverseScored: true
      },
      {
        id: 119,
        text: "I resist temptation effectively.",
        insight: "Tests practical ability to overcome sexual temptation.",
        answerFormat: "agreement"
      },
      {
        id: 120,
        text: "I avoid sexually triggering environments.",
        insight: "Shows wisdom in avoiding situations that lead to compromise.",
        answerFormat: "agreement"
      },
      {
        id: 121,
        text: "I am honest about sexual struggles.",
        insight: "Tests transparency about areas of weakness.",
        answerFormat: "agreement"
      },
      {
        id: 122,
        text: "I struggle with pornography.",
        insight: "Reveals active struggle with visual sexual content.",
        answerFormat: "agreement",
        reverseScored: true
      },
      {
        id: 123,
        text: "I value purity before marriage.",
        insight: "Tests commitment to sexual holiness during engagement.",
        answerFormat: "agreement"
      },
      {
        id: 124,
        text: "Discipline is necessary for fidelity.",
        insight: "Shows understanding that faithfulness requires built discipline.",
        answerFormat: "agreement"
      },
      {
        id: 125,
        text: "I guard my thoughts intentionally.",
        insight: "Tests mental discipline regarding sexual thoughts.",
        answerFormat: "agreement"
      },
      {
        id: 126,
        text: "I resist lustful media.",
        insight: "Reveals discipline in media consumption choices.",
        answerFormat: "agreement"
      },
      {
        id: 127,
        text: "Sexual integrity matters deeply to me.",
        insight: "Tests the priority placed on sexual purity.",
        answerFormat: "agreement"
      },
      {
        id: 128,
        text: "Marriage does not excuse lust.",
        insight: "Shows understanding that marriage doesn't cure lust.",
        answerFormat: "agreement"
      },
      {
        id: 129,
        text: "I seek holiness intentionally.",
        insight: "Tests active pursuit of sanctification.",
        answerFormat: "agreement"
      },
      {
        id: 130,
        text: "I take responsibility for boundaries.",
        insight: "Measures ownership of personal sexual boundaries.",
        answerFormat: "agreement"
      }
    ]
  },

  // ============================================================
  // MODULE 9: ENDURANCE, FORGIVENESS & RESILIENCE
  // ============================================================
  9: {
    name: "Endurance, Forgiveness & Resilience",
    shortName: "Endurance & Forgiveness",
    description: "How godly marriages survive seasons of pain, delay, and disappointment. Marriage is sustained by covenant commitment, not feelings.",
    icon: "💪",
    colorTheme: {
      primary: "#059669",
      secondary: "#D97706",
      gradient: "from-emerald-600 to-teal-700"
    },
    scoringGuidance: {
      excellent: {
        range: [57, 70],
        level: "Strong Resilience",
        message: "You demonstrate healthy patterns of endurance and forgiveness. You're prepared to weather storms together.",
        pastoralGuidance: "Continue building these muscles. The patterns you've developed will serve you well in difficult seasons."
      },
      developing: {
        range: [43, 56],
        level: "Growing Endurance",
        message: "You're developing resilience, but some areas need strengthening before facing major challenges.",
        pastoralGuidance: "Practice forgiveness in small things now. Build endurance through daily disciplines before crisis hits."
      },
      vulnerable: {
        range: [14, 42],
        level: "Resilience Needs Building",
        message: "There may be significant concerns about ability to endure difficulty or practice forgiveness consistently.",
        pastoralGuidance: "This is critical for long-term success. Address patterns of withdrawal, unforgiveness, or emotional reactivity now."
      }
    },
    questions: [
      {
        id: 131,
        text: "I forgive without keeping score.",
        insight: "Tests ability to release offenses completely.",
        answerFormat: "agreement"
      },
      {
        id: 132,
        text: "I endure discomfort without quitting.",
        insight: "Measures perseverance through difficulty.",
        answerFormat: "agreement"
      },
      {
        id: 133,
        text: "I recover emotionally after conflict.",
        insight: "Tests ability to bounce back from disagreements.",
        answerFormat: "agreement"
      },
      {
        id: 134,
        text: "I withdraw when hurt.",
        insight: "Reveals patterns of emotional shutdown.",
        answerFormat: "agreement",
        reverseScored: true
      },
      {
        id: 135,
        text: "I am patient under stress.",
        insight: "Tests emotional regulation during pressure.",
        answerFormat: "agreement"
      },
      {
        id: 136,
        text: "I practice grace consistently.",
        insight: "Measures ongoing extension of grace to partner.",
        answerFormat: "agreement"
      },
      {
        id: 137,
        text: "I do not threaten separation.",
        insight: "Tests commitment to covenant during conflict.",
        answerFormat: "agreement"
      },
      {
        id: 138,
        text: "I handle disappointment maturely.",
        insight: "Measures emotional response to unmet expectations.",
        answerFormat: "agreement"
      },
      {
        id: 139,
        text: "I believe marriage requires endurance.",
        insight: "Tests understanding that marriage is a long obedience.",
        answerFormat: "agreement"
      },
      {
        id: 140,
        text: "I respond to pain with growth.",
        insight: "Shows whether difficulty produces maturity or bitterness.",
        answerFormat: "agreement"
      },
      {
        id: 141,
        text: "I choose peace over pride.",
        insight: "Tests willingness to humble oneself for relationship.",
        answerFormat: "agreement"
      },
      {
        id: 142,
        text: "I commit even when it is hard.",
        insight: "Measures persistence through difficulty.",
        answerFormat: "agreement"
      },
      {
        id: 143,
        text: "Forgiveness is a daily practice.",
        insight: "Shows understanding that forgiveness is ongoing work.",
        answerFormat: "agreement"
      },
      {
        id: 144,
        text: "I value long-term faithfulness.",
        insight: "Tests commitment to lifelong covenant.",
        answerFormat: "agreement"
      }
    ]
  },

  // ============================================================
  // MODULE 10: BOUNDARIES, COMMUNITY & SUSTAINABILITY
  // ============================================================
  10: {
    name: "Boundaries, Community & Sustainability",
    shortName: "Boundaries & Sustainability",
    description: "Protecting marriage from external pressure and future breakdown. A strong marriage is not only built internally—it is guarded externally.",
    icon: "🏰",
    colorTheme: {
      primary: "#059669",
      secondary: "#D97706",
      gradient: "from-emerald-600 to-teal-700"
    },
    scoringGuidance: {
      excellent: {
        range: [65, 80],
        level: "Well-Protected Marriage",
        message: "You have healthy boundaries and community support. Your marriage is positioned for long-term sustainability.",
        pastoralGuidance: "Continue nurturing these protective structures. Regularly review boundaries and maintain accountability relationships."
      },
      developing: {
        range: [50, 64],
        level: "Growing Protection",
        message: "Some boundaries are in place, but others need strengthening. Community support may need to be developed.",
        pastoralGuidance: "Identify boundary gaps and address them. Build relationships with mentoring couples and maintain pastoral connection."
      },
      vulnerable: {
        range: [16, 49],
        level: "Protection Needed",
        message: "Significant boundary or community gaps exist. Without protection, your marriage is vulnerable to external pressures.",
        pastoralGuidance: "This is urgent. Establish clear boundaries, especially with family and friends. Connect with a church community that can support your marriage."
      }
    },
    questions: [
      {
        id: 145,
        text: "We have clear family boundaries.",
        insight: "Tests whether extended family roles are defined.",
        answerFormat: "agreement"
      },
      {
        id: 146,
        text: "Marriage is our top priority.",
        insight: "Measures whether marriage takes precedence over other relationships.",
        answerFormat: "agreement"
      },
      {
        id: 147,
        text: "We are accountable to mentors.",
        insight: "Tests presence of outside accountability.",
        answerFormat: "agreement"
      },
      {
        id: 148,
        text: "I resist community involvement.",
        insight: "Reveals isolation patterns that weaken marriage.",
        answerFormat: "agreement",
        reverseScored: true
      },
      {
        id: 149,
        text: "I protect time for marriage.",
        insight: "Tests intentional prioritization of couple time.",
        answerFormat: "agreement"
      },
      {
        id: 150,
        text: "I guard emotional boundaries.",
        insight: "Measures protection of emotional intimacy for spouse only.",
        answerFormat: "agreement"
      },
      {
        id: 151,
        text: "Technology does not replace intimacy.",
        insight: "Tests whether devices interfere with connection.",
        answerFormat: "agreement"
      },
      {
        id: 152,
        text: "I hide online interactions.",
        insight: "Reveals digital secrecy patterns.",
        answerFormat: "agreement",
        reverseScored: true
      },
      {
        id: 153,
        text: "We seek counsel early.",
        insight: "Tests willingness to get help before crisis escalates.",
        answerFormat: "agreement"
      },
      {
        id: 154,
        text: "I value marital check-ins.",
        insight: "Shows appreciation for regular relationship assessment.",
        answerFormat: "agreement"
      },
      {
        id: 155,
        text: "I maintain healthy friendships.",
        insight: "Tests presence of appropriate outside relationships.",
        answerFormat: "agreement"
      },
      {
        id: 156,
        text: "I avoid isolation.",
        insight: "Measures resistance to unhealthy privacy.",
        answerFormat: "agreement"
      },
      {
        id: 157,
        text: "Boundaries strengthen love.",
        insight: "Shows understanding that limits protect intimacy.",
        answerFormat: "agreement"
      },
      {
        id: 158,
        text: "We plan for long-term health.",
        insight: "Tests intentionality about marriage sustainability.",
        answerFormat: "agreement"
      },
      {
        id: 159,
        text: "I protect our covenant intentionally.",
        insight: "Measures active guarding of the marriage.",
        answerFormat: "agreement"
      },
      {
        id: 160,
        text: "I am committed to sustainability.",
        insight: "Tests dedication to lifelong marriage health.",
        answerFormat: "agreement"
      }
    ]
  }
};

// Export module names for easy reference
export const INTENSIVE_MODULE_NAMES = [
  "Kingdom Marriage Perspective",
  "Intimacy That Sustains Marriage",
  "Communication & Conflict Mastery",
  "Trust, Transparency & Accountability",
  "Expectations, Roles & Gender Needs",
  "Readiness, Red Flags & Checkpoints",
  "Purpose, Calling & Kingdom Partnership",
  "Sexual Integrity, Discipline & Fidelity",
  "Endurance, Forgiveness & Resilience",
  "Boundaries, Community & Sustainability"
];

// Helper function to get all questions for a module
export const getIntensiveModuleQuestions = (moduleNumber) => {
  return INTENSIVE_MODULE_DATA[moduleNumber]?.questions || [];
};

// Helper function to calculate module score
export const calculateIntensiveModuleScore = (answers, moduleNumber) => {
  const module = INTENSIVE_MODULE_DATA[moduleNumber];
  if (!module) return 0;
  
  let score = 0;
  module.questions.forEach(q => {
    const answer = answers[q.id];
    if (answer !== undefined) {
      score += q.reverseScored ? (6 - answer) : answer;
    }
  });
  
  return score;
};

// Get maximum possible score for a module
export const getIntensiveModuleMaxScore = (moduleNumber) => {
  const module = INTENSIVE_MODULE_DATA[moduleNumber];
  if (!module) return 0;
  return module.questions.length * 5;
};

// Get total questions count
export const getTotalIntensiveQuestions = () => {
  let total = 0;
  Object.values(INTENSIVE_MODULE_DATA).forEach(module => {
    total += module.questions.length;
  });
  return total;
};

export default INTENSIVE_MODULE_DATA;
